// @flow
import {
  takeEvery, put, call, take
} from 'redux-saga/effects';

import {
  createResource,
  deleteResource,
  updateResource
} from '@redux/components/ApiRequest/actions';

import {
  DISMISS_CONFIRMATION,
  RESPONSE_CONFIRMED,
  CONFIRM_DELETE
} from '@components/ConfirmationDialog/redux/constants';
import { displayConfirmation } from '@components/ConfirmationDialog/redux/actions';
import type { Action as DismissAction } from '@components/ConfirmationDialog/types';

import { mainApiUrl } from '@lib';

import {
  createdDocument,
  newPatientCreatedDocument,
  errorCreateDocument,
  deletedDocument,
  errorDeleteDocument,
  updatedDocument,
  errorUpdateDocument
} from './actions';

import {
  DOCUMENTS_FORM_CREATE,
  DOCUMENTS_FORM_DELETE,
  DOCUMENTS_FORM_UPDATE,
  NEW_PATIENT_DOCUMENTS_FORM_CREATE
} from './constants';

import type { Action } from '../types';

export function* documentsCreateFlow({ data }: Action): Generator<Function, void, void> {
  yield put(
    createResource(
      `${mainApiUrl}/v1/patient_files`,
      { patient_file: data },
      createdDocument,
      errorCreateDocument
    )
  );
}

export function* newPatientDocumentsCreateFlow({
  newPatientData
}: Action): Generator<Function, void, void> {
  yield put(
    createResource(
      `${mainApiUrl}/v1/patient_files/bulk_create`,
      { patient_file: newPatientData },
      newPatientCreatedDocument,
      errorCreateDocument
    )
  );
}

export function* documentsDeleteFlow({ id }: Action): Generator<Function, void, void> {
  const justId: string = id || '';

  yield put(displayConfirmation(CONFIRM_DELETE));

  const dismissedAction: ?DismissAction = yield take(DISMISS_CONFIRMATION);

  if (dismissedAction && dismissedAction.response === RESPONSE_CONFIRMED) {
    yield put(
      deleteResource(
        justId,
        `${mainApiUrl}/v1/patient_files/${justId}`,
        deletedDocument,
        errorDeleteDocument
      )
    );
  } else {
    yield put(errorDeleteDocument(justId));
  }
}

export function* documentsUpdateFlow({ id, data }: Action): Generator<Function, void, void> {
  const justId: string = id || '';

  yield put(
    updateResource(
      justId,
      `${mainApiUrl}/v1/patient_files/${justId}`,
      { patient_file: data },
      updatedDocument,
      errorUpdateDocument
    )
  );
}

export function* documentsFormFlow(action: Action): Generator<Function, void, void> {
  switch (action.type) {
    case DOCUMENTS_FORM_CREATE:
      yield call(documentsCreateFlow, action);
      break;
    case NEW_PATIENT_DOCUMENTS_FORM_CREATE:
      yield call(newPatientDocumentsCreateFlow, action);
      break;
    case DOCUMENTS_FORM_DELETE:
      yield call(documentsDeleteFlow, action);
      break;
    case DOCUMENTS_FORM_UPDATE:
      yield call(documentsUpdateFlow, action);
      break;
    default:
      break;
  }
}

function* documentsFormWatcher(): Generator<Function, void, Action> {
  yield takeEvery(
    [
      DOCUMENTS_FORM_CREATE,
      DOCUMENTS_FORM_DELETE,
      DOCUMENTS_FORM_UPDATE,
      NEW_PATIENT_DOCUMENTS_FORM_CREATE
    ],
    documentsFormFlow
  );
}

export default documentsFormWatcher;
